import { LineChartPoint } from 'components/ui/chart';
import { HOME_WEEK_CHART } from 'constants/home';
import {
  FOCUS_SUMMARY_FORM_STATE,
  TIME_UNIT_CONVERT_FACTOR
} from 'constants/general';
import { HomeWeekChartSegment, HomeWeekChartValueUnit } from 'enums';
import {
  HomeWeekChartSeries,
  HomeWeekChartTooltipParams,
  HomeWeekOverview
} from 'interfaces/home.interface';
import {
  DailySummaryStatsType,
  WeeklySummaryStatsTypes
} from 'interfaces/user.interface';
import moment from 'moment';
import { t } from 'services/i18n.service';
import { initWeeklySummaryStats, updateWeeklySummaryStats } from 'utils/helpers';
import { FOCUS_TIME_UNIT, humanizeFocusedTime } from 'utils/stats.util';
import {
  formatOrdinalDayMonth,
  formatShortDayName
} from 'utils/time.util';

type HomeWeekCompletionEvent = {
  finish_time: string;
  quantity_logged?: number;
  start_time?: string;
};

const getOverallPercentage = (day: WeeklySummaryStatsTypes): number =>
  ((day.morning_percentage ?? 0) + (day.evening_percentage ?? 0)) / 2;

const getSegmentValue = (
  day: WeeklySummaryStatsTypes,
  segment: HomeWeekChartSegment
): number => {
  switch (segment) {
    case HomeWeekChartSegment.MORNING:
      return day.morning_percentage ?? 0;
    case HomeWeekChartSegment.EVENING:
      return day.evening_percentage ?? 0;
    case HomeWeekChartSegment.FOCUS:
      return day.completed_focus_modes ?? day.focus_modes ?? 0;
    case HomeWeekChartSegment.OVERALL:
    default:
      return getOverallPercentage(day);
  }
};

export const hasHomeWeekDayActivity = (day: WeeklySummaryStatsTypes): boolean => {
  const hasRoutineProgress =
    (day.morning_percentage ?? 0) > 0 || (day.evening_percentage ?? 0) > 0;
  const hasMinutes =
    (day.morning_minutes ?? 0) > 0 || (day.evening_minutes ?? 0) > 0;
  const hasCounts =
    (day.completed_activities ?? 0) > 0 ||
    (day.completed_focus_modes ?? 0) > 0 ||
    (day.focus_modes ?? 0) > 0;
  return hasRoutineProgress || hasMinutes || hasCounts;
};

const findBestDay = (
  days: WeeklySummaryStatsTypes[]
): HomeWeekOverview['bestDay'] => {
  let bestDayScore = 0;
  let bestDayLabel = '';

  days.forEach((day) => {
    const score = getOverallPercentage(day);
    if (score <= 0 || score < bestDayScore) {
      return;
    }
    bestDayScore = score;
    bestDayLabel = new Date(day.logged_date).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  });

  if (bestDayScore <= 0) {
    return null;
  }

  return { label: bestDayLabel, completion: bestDayScore };
};

const toWeeklyStat = (
  summary: DailySummaryStatsType
): WeeklySummaryStatsTypes => ({
  date: summary.date,
  day_of_week: summary.day_of_week,
  morning_percentage: summary.morning_percentage ?? 0,
  evening_percentage: summary.evening_percentage ?? 0,
  focus_modes: summary.focus_modes ?? 0,
  morning_minutes: summary.morning_minutes ?? 0,
  morning_total_minutes: summary.morning_total_minutes ?? 0,
  evening_minutes: summary.evening_minutes ?? 0,
  evening_total_minutes: summary.evening_total_minutes ?? 0,
  micro_breaks_completion_percentage:
    summary.micro_breaks_completion_percentage ?? 0,
  micro_breaks_minutes: summary.micro_breaks_minutes ?? 0,
  micro_breaks_total_minutes: summary.micro_breaks_total_minutes ?? 0,
  total_hours_spent_in_focus_sessions:
    summary.total_hours_spent_in_focus_sessions ?? 0,
  logged_date: moment(summary.date).toDate(),
  completed_focus_modes: summary.focus_modes ?? 0,
  completed_activities: 0
});

/** Keep focus logs that fall on any day in the home week (same day rule as counters). */
export const filterFocusBlocksForHomeWeek = (
  focusBlocks: HomeWeekCompletionEvent[],
  days: WeeklySummaryStatsTypes[]
): HomeWeekCompletionEvent[] => {
  if (days.length === 0 || focusBlocks.length === 0) {
    return [];
  }
  return focusBlocks.filter((block) => {
    if (!block.finish_time) {
      return false;
    }
    const timestamp = block.start_time ?? block.finish_time;
    return days.some((day) =>
      moment(timestamp).isSame(moment(day.logged_date), 'day')
    );
  });
};

/** Temporary: overlay habit + focus counts from week summary endpoints. */
export const mergeHomeWeekCompletionCounts = ({
  days,
  activities,
  focusBlocks
}: {
  days: WeeklySummaryStatsTypes[];
  activities: HomeWeekCompletionEvent[];
  focusBlocks: HomeWeekCompletionEvent[];
}): WeeklySummaryStatsTypes[] => {
  const withActivities = updateWeeklySummaryStats(days, activities, false);
  const weekFocusBlocks = filterFocusBlocksForHomeWeek(focusBlocks, days);
  return updateWeeklySummaryStats(withActivities, weekFocusBlocks, true);
};

export const mapDailySummaryToWeeklyStats = (
  dailySummary: DailySummaryStatsType[]
): WeeklySummaryStatsTypes[] => {
  if (!dailySummary || dailySummary.length === 0) {
    return initWeeklySummaryStats(FOCUS_SUMMARY_FORM_STATE.NUMBER_OF_DAYS);
  }

  return [...dailySummary]
    .sort(
      (left, right) => moment(left.date).valueOf() - moment(right.date).valueOf()
    )
    .map(toWeeklyStat);
};

export const buildHomeWeekChartSeries = ({
  days,
  segment,
  language
}: {
  days: WeeklySummaryStatsTypes[];
  segment: HomeWeekChartSegment;
  language?: string;
}): HomeWeekChartSeries => {
  const points: LineChartPoint[] = (days ?? []).map((day) => ({
    label: formatShortDayName(day.logged_date, language),
    value: getSegmentValue(day, segment)
  }));

  const valueUnit =
    segment === HomeWeekChartSegment.FOCUS
      ? HomeWeekChartValueUnit.COUNT
      : HomeWeekChartValueUnit.PERCENT;

  return { points, valueUnit };
};

export const isHomeWeekChartSeriesEmpty = (points: LineChartPoint[]): boolean =>
  points.length === 0 ||
  points.every((point) => point.value === null || point.value === 0);

export const formatHomeWeekDateRange = (
  days: WeeklySummaryStatsTypes[]
): string => {
  if (!days || days.length === 0) {
    return t(HOME_WEEK_CHART.TITLE_KEY);
  }

  const start = formatOrdinalDayMonth(days[0].logged_date);
  const end = formatOrdinalDayMonth(days[days.length - 1].logged_date);

  if (!start || !end) {
    return t(HOME_WEEK_CHART.TITLE_KEY);
  }

  return `${start} - ${end}`;
};

export const formatHomeWeekChartTooltip = ({
  segment,
  value,
  day
}: HomeWeekChartTooltipParams): string => {
  if (segment === HomeWeekChartSegment.FOCUS) {
    return t('weekly_stats.tooltip_focus_value', {
      count: Math.round(value)
    });
  }

  const percentValue = t('weekly_stats.tooltip_percent_value', {
    value: Math.round(value)
  });

  if (segment !== HomeWeekChartSegment.OVERALL || !day) {
    return percentValue;
  }

  return t('weekly_stats.tooltip_overall_with_minutes', {
    value: Math.round(value),
    morningMinutes: Math.round(day.morning_minutes ?? 0),
    morningTotal: Math.round(day.morning_total_minutes ?? 0),
    eveningMinutes: Math.round(day.evening_minutes ?? 0),
    eveningTotal: Math.round(day.evening_total_minutes ?? 0)
  });
};

export const getHomeWeekChartSegmentOptions = () =>
  HOME_WEEK_CHART.SEGMENTS.map((segment) => ({
    value: segment.value,
    label: t(segment.labelKey)
  }));

export const buildHomeWeekOverview = (
  days: WeeklySummaryStatsTypes[]
): HomeWeekOverview | null => {
  if (!days || days.length === 0) {
    return null;
  }

  const daysWithActivity = days.filter(hasHomeWeekDayActivity);
  const baseDays = daysWithActivity.length > 0 ? daysWithActivity : days;
  const dayCount = baseDays.length || 1;

  const totals = baseDays.reduce(
    (acc, day) => {
      acc.morningPercentage += day.morning_percentage ?? 0;
      acc.eveningPercentage += day.evening_percentage ?? 0;
      acc.completedActivities += day.completed_activities ?? 0;
      acc.completedFocusModes +=
        day.completed_focus_modes ?? day.focus_modes ?? 0;
      acc.totalFocusHours += day.total_hours_spent_in_focus_sessions ?? 0;
      return acc;
    },
    {
      morningPercentage: 0,
      eveningPercentage: 0,
      completedActivities: 0,
      completedFocusModes: 0,
      totalFocusHours: 0
    }
  );

  const averageMorning = totals.morningPercentage / dayCount;
  const averageEvening = totals.eveningPercentage / dayCount;

  return {
    averageMorning,
    averageEvening,
    averageOverall: (averageMorning + averageEvening) / 2,
    totalHabits: totals.completedActivities,
    habitsPerDay: totals.completedActivities / dayCount,
    totalFocusSessions: totals.completedFocusModes,
    totalFocusHours: totals.totalFocusHours,
    bestDay: findBestDay(days)
  };
};

export const formatHomeWeekFocusSessionsLabel = (
  totalFocusHours: number
): string => {
  if (!(totalFocusHours > 0)) {
    return t('weekly_stats.focus_sessions');
  }

  const humanized = humanizeFocusedTime(
    totalFocusHours * TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES
  );

  if (humanized.unit === FOCUS_TIME_UNIT.MINUTES) {
    return t('weekly_stats.focus_sessions_with_minutes', {
      minutes: humanized.value
    });
  }

  return t('weekly_stats.focus_sessions_with_hours', {
    hours: getHomeWeekFocusHoursLabelValue(humanized)
  });
};

const getHomeWeekFocusHoursLabelValue = (humanized: {
  value: number;
  unit: (typeof FOCUS_TIME_UNIT)[keyof typeof FOCUS_TIME_UNIT];
  remainingHours?: number;
}): number => {
  if (humanized.unit === FOCUS_TIME_UNIT.HOURS) {
    return humanized.value;
  }

  const hoursPerDay =
    TIME_UNIT_CONVERT_FACTOR.ONE_DAY_AS_SECONDS /
    TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS;

  return humanized.value * hoursPerDay + (humanized.remainingHours ?? 0);
};
